import { isValid, parse } from 'date-fns';
import {
  COOKIE_NAMES,
  MARKETING_CONSENT_STATUS,
  OPTED_OUT_WELCOME_OFFER_ID,
  REGISTRATION_FLOW_ENDPOINTS,
} from './constants.ts';
import { getCookieData, storeInRawCookie } from './cookies.ts';
import { FLOW_NAMES } from './countries.ts';
import { API_URL, API_URL_SPAIN, URL } from './envConstants.ts';
import { gatekeeper } from './gatekeeper.ts';
import { MARKETS } from './markets.ts';
import { TCmsFlow } from '../cms';
import { TDateFormat } from '../components/inputs/dateInput.tsx';
import { TDynamicStoreField } from '../store/store.ts';
import {
  Limit,
  MarketingConsent,
  TBirthDate,
  TCardField,
  TCurrency,
  TField,
  TLimitType,
  TMarketingConsentStatus,
  TRegistrationDoneGTM,
} from '../types/types.ts';

export const flattenFields = (fields: TField[]): TField[] => {
  let allFields: TField[] = [];

  fields?.forEach((field) => {
    if ('fields' in field) {
      allFields = allFields.concat(flattenFields((field as TCardField).fields));
    } else {
      allFields.push(field);
    }
  });

  return allFields;
};

export const getPreviousKeyValue = (obj: { [x: string]: string }, targetKey: string) => {
  let previousKey = null;
  let lastKey = null;

  for (const key of Object.keys(obj)) {
    if (key === targetKey) {
      if (previousKey !== null) {
        return obj[previousKey];
      } else {
        return null;
      }
    }
    previousKey = key;
    lastKey = key;
  }

  return lastKey !== null ? obj[lastKey] : null;
};

export type HasName = {
  name: string;
}

export const filterAndSortCountries = <T extends HasName>(countries: T[], searchValue: string): T[] => {
  if (!countries || !searchValue) return countries;

  const lowerSearchValue = searchValue.toLowerCase();
  const startsWithSearchValue = countries.filter((country) =>
    country.name.toLowerCase().startsWith(lowerSearchValue),
  );
  const includesSearchValue = countries.filter((country) =>
    !country.name.toLowerCase().startsWith(lowerSearchValue) &&
    country.name.toLowerCase().includes(lowerSearchValue),
  );

  return [...startsWithSearchValue, ...includesSearchValue];
};

interface TAddress {
  houseNumber: string;
  street: string;
  city: string;
  postcode: string;
  province: string;
  provinceCode: string;
}

export const mapDetailedAddress = (detailedAddress: google.maps.GeocoderResult): TAddress => {
  const addressComponents = detailedAddress.address_components;

  const cityTypes = ['postal_town', 'locality'];
  const cityType = cityTypes.find(type =>
    addressComponents.some(component => component.types.includes(type)),
  ) ?? '';

  const getComponent = (type: string, shortName?: boolean): string => {
    const component = addressComponents.find((comp) => comp.types.includes(type));

    return component
      ? shortName
        ? component.short_name
        : component.long_name
      : '';
  };

  const getFirstAvailableComponent = (types: string[], shortName?: boolean): string => {
    for (const type of types) {
      const value = getComponent(type, shortName);
      if (value) return value;
    }

    return '';
  };

  return {
    houseNumber:  getComponent('street_number'),
    street:       getComponent('route'),
    city:         getComponent(cityType),
    postcode:     getComponent('postal_code'),
    province:     getFirstAvailableComponent(['administrative_area_level_1', 'administrative_area_level_2']),
    provinceCode: getFirstAvailableComponent(['administrative_area_level_1', 'administrative_area_level_2'], true),
  };
};

// granted = every channel opted in, partial = at least one, denied = none
export const getMarketingConsentStatus = (
  marketingConsent?: MarketingConsent | boolean | null,
): TMarketingConsentStatus => {
  if (typeof marketingConsent === 'boolean') {
    return marketingConsent ? MARKETING_CONSENT_STATUS.GRANTED : MARKETING_CONSENT_STATUS.DENIED;
  }

  const channels = Object.values(marketingConsent ?? {}).flatMap((vertical) => Object.values(vertical ?? {}));
  const grantedCount = channels.filter(Boolean).length;

  if (!grantedCount) return MARKETING_CONSENT_STATUS.DENIED;

  return grantedCount === channels.length ? MARKETING_CONSENT_STATUS.GRANTED : MARKETING_CONSENT_STATUS.PARTIAL;
};

export const registrationCompleteTracking = (
  registrationPayload?: TDynamicStoreField,
  playerId?: string,
): TRegistrationDoneGTM => {
  const woId = registrationPayload?.welcomeOffer || OPTED_OUT_WELCOME_OFFER_ID;

  return {
    email: registrationPayload?.email,
    countryResidence:
      registrationPayload?.countryOfResidence || registrationPayload?.country || '',
    wo_used:
      !!registrationPayload?.welcomeOffer &&
      registrationPayload?.welcomeOffer !== OPTED_OUT_WELCOME_OFFER_ID,
    woId,
    // Duplicate of woId, kept until the naming is consolidated (PSUP-673)
    wo_id:              woId,
    dailyDepositLimit:  registrationPayload?.limits?.daily?.value || undefined,
    weeklyDepositLimit: registrationPayload?.limits?.weekly?.value || undefined,
    monthlyDepositLimit:
      registrationPayload?.limits?.monthly?.value || undefined,
    marketingConsent:  registrationPayload?.marketingConsent,
    marketing_consent: getMarketingConsentStatus(registrationPayload?.marketingConsent),
    userId:            playerId,
    // Temporary duplicate of userId, kept until GTM is migrated (PSUP-673)
    playerId,
    nationality:
      registrationPayload?.nationality || '',
    firstName: registrationPayload?.firstName,
    lastName:  registrationPayload?.lastName,
  };
};

export const clearDataInLocalStorage = (market: string) => {
  try {
    localStorage?.removeItem(`data_${market}`);
    localStorage?.removeItem(`step_${market}`);
    localStorage?.removeItem(`metadata_${market}`);
    localStorage?.removeItem(`steps_skipped_${market}`);
  } catch (error) {
    console?.error('Error clearing data in localStorage:', error);
  }
};

export const resetProgressInLocalStorage = (market: string) => {
  try {
    localStorage?.setItem(`step_${market}`, '1');
    localStorage?.removeItem(`steps_skipped_${market}`);
  } catch (error) {
    console?.error('Error resetting registration progress in localStorage:', error);
  }
};

const getPath = (market: string) => {
  const registrationEndpointsMap = REGISTRATION_FLOW_ENDPOINTS as unknown as Record<string, string>;

  return registrationEndpointsMap[market] || registrationEndpointsMap['default'];
};

export const getRegisterUrl = (market: string) => {
  const path = getPath(market);
  switch (market) {
  case MARKETS.ES:
    return `${API_URL_SPAIN}/casino-player/registration-service/api/v1/dgoj/register/es`
    // WE WILL GO WITH THE SAME ENDPOINT AS IE AT THE BEGENNING
    // case MARKETS.GI:
    //   return `${API_URL}/casino-player/registration-service/api/v1/gra/register`
  default:
    return `${API_URL}/casino-player/registration-service/api/v1/${path}`
  }
}

export const getPnpRegisterUrl = (market: string) =>
  `${API_URL}/casino-player/pay-and-play-orchestrator/api/${market}/register`;

export const getPnpLoginUrl = () =>
  `${API_URL}/casino-player/pay-and-play-orchestrator/api/login`;

export const getMarketLobbyUrl = (): string => {
  const market = gatekeeper?.localisation?.marketSlug;

  if (!market || market === 'es') return `${URL}/`;

  return `${URL}/${market}`;
};

export const colorToRgba = (color: string, alpha = 1) => {
  if (color.startsWith('rgba')) {
    return color; // Return it as is if it's already rgba
  }

  color = color.replace(/^#/, '');
  let r, g, b;

  if (color.length === 3) {
    r = parseInt(color[0] + color[0], 16);
    g = parseInt(color[1] + color[1], 16);
    b = parseInt(color[2] + color[2], 16);
  } else if (color.length === 6) {
    r = parseInt(color.slice(0, 2), 16);
    g = parseInt(color.slice(2, 4), 16);
    b = parseInt(color.slice(4, 6), 16);
  } else {
    console.error('Invalid color color format');
  }

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export const separateBirthdate = (birthdate: string) => {
  const formats: TDateFormat[] = ['dd/MM/yyyy', 'MM/dd/yyyy', 'yyyy/MM/dd'];

  let parsedDate: Date | null = null;

  for (const format of formats) {
    const attempted = parse(birthdate, format, new Date());
    if (isValid(attempted)) {
      parsedDate = attempted;
      break;
    }
  }

  if (!parsedDate) {
    throw new Error(`Unable to parse date: ${birthdate}`);
  }

  const birthday: TBirthDate = {
    dayOfBirth:   parsedDate.getDate(),
    monthOfBirth: parsedDate.getMonth() + 1,
    yearOfBirth:  parsedDate.getFullYear(),
  };

  return birthday;
};

export const getDepositLimitData = (
  daily: string,
  weekly: string,
  monthly: string,
  currency: TCurrency,
): Limit | null => {
  let depositType: TLimitType = 'DAILY';

  if (!daily && !weekly && !monthly) return null;
  if (monthly) depositType = 'MONTHLY';
  if (weekly) depositType = 'WEEKLY';

  return {
    depositLimit: {
      currency: currency || 'EUR',
      value:    daily || weekly || monthly,
    },
    depositType,
  }
}

const validateChecksum = (idNumber: string, isNIE: boolean = false): boolean => {
  const controlLetters = 'TRWAGMYFPDXBNJZSQVHLCKE';
  let numericPart = idNumber.slice(0, -1);

  if (isNIE) {
    const letterMap = { 'X': '0', 'Y': '1', 'Z': '2' };
    numericPart = letterMap[idNumber[0] as keyof typeof letterMap] + idNumber.slice(1, -1);
  }

  const expectedLetter = controlLetters[parseInt(numericPart, 10) % 23];

  return idNumber.slice(-1) === expectedLetter;
};

export const checkIdentificationNumberES = (identificationNumber: string, numberType: string): boolean => {
  if (!identificationNumber) return false;

  const dniRegex = /^\d{8}[TRWAGMYFPDXBNJZSQVHLCKE]$/;
  const nieRegex = /^[XYZ]\d{7}[TRWAGMYFPDXBNJZSQVHLCKE]$/;

  if (numberType === 'DNI' && dniRegex.test(identificationNumber)) {

    return validateChecksum(identificationNumber);
  }

  if (numberType === 'NIE' && nieRegex.test(identificationNumber)) {

    return validateChecksum(identificationNumber, true);
  }

  return false;
};

export const interpolate = (
  target: string = '',
  replacements: Record<string, string | number>,
  preservePlaceholder = true,
) => {
  if (!target) return target;

  const INTERPOLATION_REGEX = /{{1}\s*(\w+)\s*}{1}/gm;
  const CURRENCY_INTERPOLATION_REGEX = /{{1}\s*(\w+)\s* \|\s*€\s*}{1}/gm;
  const TEMPLATE_STRING_INTERPOLATION_REGEX = /\${(\w+)}/gm;
  const BRACKET_INTERPOLATION_REGEX = /\[{1,2}\s*(\w+)\s*\]{1,2}/gm;

  const replacer = (match: string, param: string) => {
    if (param in replacements) {
      return String(replacements[param]);
    }

    return preservePlaceholder ? match : '';
  };

  return target
    .replace(INTERPOLATION_REGEX, replacer)
    .replace(CURRENCY_INTERPOLATION_REGEX, replacer)
    .replace(TEMPLATE_STRING_INTERPOLATION_REGEX, replacer)
    .replace(BRACKET_INTERPOLATION_REGEX, replacer);
};

export const getCmapiUrl = (market: string) => {
  let url = '';
  switch (market) {
  case MARKETS.ES:
    url = API_URL_SPAIN;
    break
  default:
    url = API_URL;
    break;
  }

  return `${url}/casino-player/registration-service/api/v1/cookie-consent`
}

export const extractQueryFromCurrentRoute = (currentRouteUnknown: unknown): string => {
  if (typeof currentRouteUnknown !== 'object' || currentRouteUnknown === null) {
    return '';
  }

  const maybeQuery = (currentRouteUnknown as { query?: unknown }).query;

  return typeof maybeQuery === 'string' && maybeQuery.length > 0 ? maybeQuery : '';
};

export const isCountryResidenceStepSkippedForUserLocation = (market: TCmsFlow): boolean => {
  const userCountryCode = getCookieData(COOKIE_NAMES.KAZUMOKEYCLOAK, 'country');

  return userCountryCode === 'GB' && market.name === FLOW_NAMES.UKGC && market.steps.length > 1;
};

export const filterFlowByUserLocation = (market: TCmsFlow): TCmsFlow => {
  if (isCountryResidenceStepSkippedForUserLocation(market)) {
    return {
      ...market,
      steps: market.steps.slice(1),
    };
  }

  return market;
};

export const setWelcomeLoginCookie = () => {
  const options = { expires: 1 };

  storeInRawCookie(COOKIE_NAMES.WELCOME_LOGIN, 'true', options);
};
